import React, { useEffect, useMemo, useContext } from "react";
import "./QueuesSummaryKPI.scss";
import { useHistory } from "react-router-dom";
import WidgetError from "../../../WidgetError/WidgetError";
import {
  Loader,
  TextButton,
  useIcon,
  formatNumber,
  ModalContext,
} from "@SeemplicityDev/shared";
import EntityList, {
  type EntityItem,
} from "../../../../../Components/EntityLogo/EntityList";
import useQueryWithRetries from "../../../../../Tools/useQueryWithRetries";
import { GET_QUEUE_SUMMARY } from "../../../../WidgetDataApi";
import AddAutomation from "../../../../../Remediation/AddAutomation/AddAutomation";
import { integrationTypeToPageLinkMap } from "@SeemplicityDev/ticket-master";
import type { SelectedScope } from "../../../../../types/scope";

interface QueueTypeMeta {
  title: string;
  order: number;
}

const queueTypes: Record<string, QueueTypeMeta> = {
  TICKET: { title: "Ticketing Queues", order: 1 },
  REPORT: { title: "Notifications", order: 2 },
  INTERNAL: { title: "Views", order: 3 },
};

// Raw response shape of the GET_QUEUE_SUMMARY query. `data` and `metadata` are
// JSON-encoded strings parsed at runtime below.
interface QueueSummaryQueryData {
  generic_widget_data?: {
    data?: string;
    metadata?: string;
  };
}

// Parsed `data` payload: maps a provider key to its queue count.
type QueueRawData = Record<string, number>;
// Parsed `metadata.ticket_provider_type_to_endpoint_type`: maps a provider key
// to its provider type (one of the `queueTypes` keys, e.g. TICKET/REPORT/INTERNAL).
type QueueMetadata = Record<string, string>;

interface QueueProviderEntity {
  displayName: string;
  logo: string | undefined;
  count: number;
}

interface QueueProviderTypeGroup {
  id: string;
  name: string | undefined;
  totalCount: number;
  byProvider: QueueProviderEntity[];
}

interface QueuesSummaryKPIProps {
  selectedScope?: SelectedScope | null;
  scopeToExecute?: string | null;
  filterToExecute?: string | null;
  isForExport?: boolean;
  setIsReadyToExport?: ((isReady: boolean) => void) | null;
}

const QueuesSummaryKPI: React.FC<QueuesSummaryKPIProps> = ({
  selectedScope,
  scopeToExecute,
  filterToExecute,
  isForExport,
  setIsReadyToExport,
}) => {
  const { getSvg, getDataSourceIcon } = useIcon(
    isForExport ? "LIGHT" : undefined
  );
  const { openModal } = useContext(ModalContext);
  const { push } = useHistory();
  const { data, error, loading, refetch } = useQueryWithRetries({
    query: GET_QUEUE_SUMMARY,
    variables: {
      widget_definition_key: "queues_count_by_endpoint_type",
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });

  const processedData = useMemo<QueueProviderTypeGroup[] | null>(() => {
    if (!!data) {
      // useQueryWithRetries exposes `data` untyped (null-init state); narrow to
      // the query response shape before reading the JSON-string fields.
      const queueData = data as QueueSummaryQueryData;
      let rawData: QueueRawData = {};
      let metadata: QueueMetadata = {};

      try {
        rawData = JSON.parse(queueData?.generic_widget_data?.data ?? "");
        metadata = JSON.parse(
          queueData?.generic_widget_data?.metadata ?? ""
        )?.ticket_provider_type_to_endpoint_type;
      } catch (e) {
        console.error("Failed to parse queues summary data", e);
      }

      return Object.entries(metadata)
        ?.reduce<QueueProviderTypeGroup[]>((acc, [provider, providerType]) => {
          const currentType = acc.find((type) => type.id === providerType);
          const providerCount = rawData[provider];
          if (!currentType) {
            acc.push({
              id: providerType,
              name: queueTypes[providerType]?.title,
              totalCount: providerCount || 0,
              byProvider: [
                ...(!!providerCount
                  ? [
                      {
                        displayName: provider,
                        logo: getDataSourceIcon(provider, "S") as
                          | string
                          | undefined,
                        count: providerCount,
                      },
                    ]
                  : []),
              ],
            });
          } else {
            if (!!providerCount) {
              currentType.totalCount += providerCount;
              currentType.byProvider.push({
                displayName: provider,
                logo: getDataSourceIcon(provider, "S") as string | undefined,
                count: providerCount,
              });
            }
          }

          return acc;
        }, [])
        ?.sort(
          (a, b) =>
            (queueTypes[a.id]?.order ?? 0) - (queueTypes[b.id]?.order ?? 0)
        );
    }

    return null;
  }, [data, getDataSourceIcon]);

  useEffect(() => {
    setIsReadyToExport?.(!!processedData);
  }, [processedData, setIsReadyToExport]);

  useEffect(() => {
    if (!!error) {
      setIsReadyToExport?.(true);
    }
  }, [error]);

  useEffect(() => {
    setIsReadyToExport?.(!loading);
  }, [loading]);

  return (
    <div className={"queues-summary"}>
      {!!error ? (
        <div className={"error-wrapper"}>
          <WidgetError onWidgetsReload={refetch} />
        </div>
      ) : loading ? (
        <Loader
          center
          img={getSvg("icon-spinner")}
          className={"loading-wrapper"}
        />
      ) : (
        !!processedData?.length && (
          <>
            {processedData?.map((providerType) => {
              return (
                <div className={"queues-summary-type"} key={providerType.id}>
                  <span className="type-total-count">
                    {!!providerType.totalCount
                      ? formatNumber(providerType.totalCount)
                      : 0}
                  </span>
                  <span className="type-name">{providerType.name}</span>
                  {!!providerType?.byProvider?.length ? (
                    <EntityList
                      list={providerType.byProvider as EntityItem[]}
                      maxToDisplay={!isForExport ? 3 : null}
                      noGap
                      onEntityClick={(entity) => {
                        push(
                          `/remediation/monitoring/${integrationTypeToPageLinkMap[providerType.id]}`
                        );
                      }}
                    />
                  ) : !isForExport ? (
                    <TextButton
                      label={"Connect"}
                      onClick={() => {
                        openModal({
                          title: "Add Automation",
                          size: "xl",
                          overlay: true,
                          className: "modal-add-automation",
                          component: <AddAutomation />,
                        });
                      }}
                    />
                  ) : (
                    <span className={"caption"}>Connect with Seemplicity</span>
                  )}
                </div>
              );
            })}
          </>
        )
      )}
    </div>
  );
};

export default QueuesSummaryKPI;
